// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import styled, { css } from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import I18n from 'APP/I18n'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { useDevTools } from 'APP/Lib/DevTools'

import PatientActions from 'APP/Redux/PatientRedux'
import LoginActions from 'APP/Redux/LoginRedux'
import type { FamilyMember } from 'APP/Store/Family/types'

import { Images } from 'APP/Themes'

import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import Modal from 'APP/Components/Modal'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import LogoHeader from 'APP/Components/LogoHeader'
import { LoadingOverlay } from 'APP/Components/LoadingOverlay'

import ConsentForm from './ConsentForm'
import DeclineAcceptButtonBar from './DeclineAcceptButtonBar'

import { FormState, ConsentState } from './types'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { selectIsAppOnline } from 'APP/Sagas/AppSessionSagas'
import type { PatientProfileState } from 'APP/Store/Patient/types'
import { FamilyMemberType } from 'APP/Services/Scribe/types'

const Container = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  flex: 1;
`

const SafeViewContainer = styled(SafeAreaView).attrs({
  edges: ['bottom'],
})`
  flex: 1;
`

const ContentContainer = styled.View<{ submitDisabled: boolean }>`
  flex: 1;
  ${({ submitDisabled }) => {
    if (isWeb()) {
      return css`
        min-height: ${submitDisabled ? '250px' : '50px'};
      `
    }
  }};
`

const ButtonsContainer = styled.View`
  padding-bottom: ${({ theme }) =>
    isWeb() && theme.isSmallScreen ? theme.metrics.baseMargin / 2 : 0}px;
`

const Divider = styled.View`
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.disabled};
`

// FIXME: select only the properties of the `member` object that we need
// This method can receive either a PatientProfileState or a NormalizedFamilyMember
// Those two types are compatible as inputs to memberToFormState somewhat by accident,
// but they make the response of an unpredictable and inconsistent type
const memberToFormState = (
  member: FormState['member'],
  userProfile: FormState['member']
): FormState => {
  if (member?.type === FamilyMemberType.Child) {
    return {
      member,
      loaded: null,
      state: null,
      uri: UrlBuilder.guardianConsentUrl(
        userProfile?.givenName,
        userProfile?.familyName,
        member?.givenName,
        member?.familyName
      ),
    }
  }

  return {
    member,
    loaded: false,
    state: ConsentState.Displayed,
    uri: UrlBuilder.consentUrl(),
  }
}

const familyStateToDependentFormSet = (
  familyMembers: Record<string, FamilyMember> = {},
  userProfile: PatientProfileState
) =>
  Object.values(familyMembers)
    .filter(({ type }) => type === FamilyMemberType.Child)
    .sort((a) => (a.type === FamilyMemberType.Child ? 1 : -1))
    .map((member) => memberToFormState(member, userProfile))

export type ConsentScreenProps = NativeStackScreenProps<ReactNavigation.RootParamList, 'consent'>

const ConsentScreen = ({ route }: ConsentScreenProps) => {
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const dispatch = useDispatch()
  const { setToolFunction, setToolName } = useDevTools()

  const { profile, giveConsentRunning } = useSelector((state) => state.patient)
  const isConnected = useSelector(selectIsAppOnline)
  const {
    members: familyMembers,
    requestRunning: fetchFamilyRunning,
    requestError: fetchFamilyError,
  } = useSelector((state) => state.family)

  const initialForms = useMemo(() => {
    return [
      memberToFormState(profile, {
        type: FamilyMemberType.Child,
      }),
      ...familyStateToDependentFormSet(familyMembers, profile),
    ]
  }, [familyMembers, profile])

  const [forms, setForms] = useState(initialForms)

  useEffect(() => {
    if (forms.length !== initialForms.length) {
      setForms(initialForms)
    }
  }, [initialForms, forms.length])

  const setFormStateIndex = (index: number, state: ConsentState) => {
    const newForms = [...forms]
    newForms[index].state = state
    setForms(newForms)
  }

  useEffect(() => {
    setToolName('Skip')
    setToolFunction(() => () => {
      dispatch(PatientActions.patientProfileGiveConsent(forms))
    })
    return () => {
      setToolName(null)
      setToolFunction(null)
    }
  }, [forms, dispatch])

  const openAcceptModal = () => setShowAcceptModal(true)
  const closeAcceptModal = () => setShowAcceptModal(false)
  const openDeclineModal = () => setShowDeclineModal(true)
  const closeDeclineModal = () => setShowDeclineModal(false)

  const handleAgreeOnAcceptPress = () => {
    closeAcceptModal()
    dispatch(PatientActions.patientProfileGiveConsent(forms))
  }

  const handleAgreeOnDeclinePress = () => {
    closeDeclineModal()
    dispatch(PatientActions.patientProfileDeclineConsent(forms))
    dispatch(LoginActions.logout('Declined consent', true))
  }

  const handleGiveConsentPress = () => {
    openAcceptModal()
  }

  const handleDeclineConsentPress = () => {
    openDeclineModal()
  }

  const getSubmitDisabledState = () =>
    !isConnected ||
    !!fetchFamilyError ||
    forms.length === 0 ||
    !!forms.find((form) => form.state === null || form.state === ConsentState.Displayed)

  const flow = route.params.flow

  const showLoadingIndicator = fetchFamilyRunning || giveConsentRunning

  return (
    <>
      <NoticeBarContainer>
        <SafeViewContainer>
          <Container>
            <LogoHeader
              source={Images.logoConsent}
              title={I18n.t(`ConsentScreen.${flow}.title`)}
              subtitle={I18n.t(`ConsentScreen.${flow}.subtitle`)}
            />

            <ContentContainer testID="CONTENT_CONTAINER" submitDisabled={getSubmitDisabledState()}>
              <ResponsiveScrollView>
                {forms.map((form, index) => (
                  <ConsentForm
                    key={index}
                    form={form}
                    setState={(state) => setFormStateIndex(index, state as ConsentState)}
                  />
                ))}
              </ResponsiveScrollView>
            </ContentContainer>
            <ButtonsContainer>
              <Divider />
              <DeclineAcceptButtonBar
                flow={flow}
                onDecline={handleDeclineConsentPress}
                onAccept={handleGiveConsentPress}
                submitDisabled={getSubmitDisabledState()}
              />
            </ButtonsContainer>
          </Container>
        </SafeViewContainer>
        <Modal
          statusBarTranslucent
          visible={showAcceptModal}
          transparent
          title={I18n.t('ConsentScreen.finishPopup.title')}
          subtitle={I18n.t('ConsentScreen.finishPopup.body')}
          primaryActionText={I18n.t('ConsentScreen.finishPopup.agree')}
          secondaryActionText={I18n.t('ConsentScreen.finishPopup.decline')}
          handleCloseModal={closeAcceptModal}
          handlePrimaryAction={handleAgreeOnAcceptPress}
          handleSecondaryAction={closeAcceptModal}
        />
        <Modal
          statusBarTranslucent
          visible={showDeclineModal}
          transparent
          title={I18n.t('ConsentScreen.cancelPopup.title')}
          subtitle={I18n.t('ConsentScreen.cancelPopup.body')}
          secondaryActionText={I18n.t('ConsentScreen.cancelPopup.agree')}
          primaryActionText={I18n.t('ConsentScreen.cancelPopup.decline')}
          handleCloseModal={closeDeclineModal}
          handleSecondaryAction={handleAgreeOnDeclinePress}
          handlePrimaryAction={closeDeclineModal}
        />
      </NoticeBarContainer>
      {showLoadingIndicator && <LoadingOverlay color="#FFFFFF" />}
    </>
  )
}

export default ConsentScreen
